import {
  Component,
  OnInit,
  OnChanges,
  Input,
  SimpleChanges,
  Output,
  EventEmitter
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { InbetriebnahmeService } from '../../../../Services/inbetriebnahme.service';
import { CustomerService } from '../../../../Services/customer.service';
import { Customer } from '../../../../Models/customer.model';
import { VorbereiteterKiImport } from '../../../../Models/vorbereiteter-ki-import.model';
import {
  AnlagenteilWert,
  ExcelAiAnalysis,
  ExcelAiColumn,
  ExcelAiSheetAnalysis,
  ExcelPreview,
  ExcelSheetPreview,
  FELD_KENNZEICHNUNG,
  ImportRegeln,
  ImportVorschau,
  ImportVorschauSpalte,
  ImportVorschauZeile,
  KategorieVorschlag,
  KENNZEICHNUNGS_ARTEN,
  LISTEN_TYPEN,
  MessstelleKategorie,
  REGEL_OPERATOREN,
  RegelOperator,
  ZeilenEntscheidung,
  ZeilenRelevanzEintrag,
  ZeilenRelevanzErgebnis,
  kennzeichnungLabel,
  normalisiereStandardFeld,
  standardFelder,
  ListenTyp
} from '../../../../Models/messstelle.model';

/**
 * Eine Regel, wie sie im Importdialog formuliert wird.
 *
 * Sie wird zusammen mit der Datei an die Vorschau geschickt und dort auf ALLE
 * Zeilen angewendet. Die Trefferzahl ist deshalb echt und nicht aus zehn
 * Beispielzeilen geschätzt – und sie stammt aus derselben Rechnung, die später
 * der Import ausführt.
 */
interface Regelentwurf {
  feld: string;
  operator: RegelOperator;
  wert: string;

  /** Trefferzahl aus der letzten Vorschau. Null = noch nicht gerechnet. */
  treffer: number | null;
}

/** Eine Regel, die eine Kategorie zuweist. */
interface KategorieRegelentwurf extends Regelentwurf {
  kategorieId: number | null;
}

/**
 * Eine Regel, die einen Anlagenteil zuweist – angesprochen über den NAMEN.
 * Vor dem Import gibt es die Anlagenteile noch nicht; sie entstehen erst
 * beim Speichern, und zwar nur die, die auch getroffen wurden.
 */
interface AnlagenteilRegelentwurf extends Regelentwurf {
  anlagenteilName: string;
}


/**
 * Eine Zeile, wie die Vorschautabelle sie zeichnet.
 *
 * Sie führt Werte und Beurteilung zusammen, damit es NUR EINE Tabelle gibt –
 * im Ansehen wie im Bearbeiten. Der Zustand selbst liegt weiterhin
 * ausschließlich in `status`, einer Referenz auf den Eintrag in
 * `relevanz.zeilen`. Hier wird nichts kopiert, was sich ändern kann.
 */
interface VorschauAnzeigeZeile {
  rowNumber: number;
  werte: { [column: string]: string };
  kategorieName?: string | null;
  anlagenteilName?: string | null;

  /** Null, solange keine Zeilenprüfung gelaufen ist – dann gilt "wird importiert". */
  status: ZeilenRelevanzEintrag | null;
}


/**
 * Import einer Messstellen- oder Verbraucherliste.
 *
 * Der Ablauf, wie ihn der Benutzer erlebt:
 *
 * 1. Excel hochladen.
 * 2. Die Erkennung läuft und meldet in SECHS ZEILEN, was sie gefunden hat.
 *    Wer will, klappt „Erkennung bearbeiten“ auf und korrigiert alles –
 *    Listentyp, Blatt, Kennzeichnungsart, Kopfbereich.
 * 3. Anlagenteile und Kategorien stehen kompakt da und lassen sich auf
 *    Wunsch prüfen.
 * 4. Die VORSCHAU ist der Hauptteil: so wird die Liste aussehen. Sie ist
 *    reine Anzeige.
 * 5. Nur bei Bedarf: „Spalten anpassen“ oder „Zeilenauswahl bearbeiten“.
 * 6. Importieren.
 *
 * ENTSCHEIDEND: bis Schritt 6 entsteht in der Datenbank NICHTS. Die Vorschau
 * rechnet serverseitig mit derselben Logik, die der Import später ausführt,
 * schreibt aber nicht.
 *
 * Die Erkennung ist eine Hilfe, keine Entscheidung: schlägt sie fehl, bleibt
 * der manuelle Weg über Blatt und Kopfzeile unverändert bestehen.
 */
@Component({
  selector: 'app-messstellen-import',
  templateUrl: './messstellen-import.component.html',
  styleUrls: ['./messstellen-import.component.css']
})
export class MessstellenImportComponent implements OnInit, OnChanges {

  @Input() eingebettet = false;
  @Input() importDatei: File | null = null;
  @Input() importProjektId: number | null = null;
  @Input() importProjektName = '';

  /** Schlüssel der Datei im Mehrfachimport – damit der Assistent diese Instanz wiederfindet. */
  @Input() importSchluessel: string | null = null;

  /**
   * Vom Assistenten gewählter Listentyp (Messstellen-/Verbraucherliste). Er
   * überschreibt die KI-Erkennung des Typs – die Spaltenzuordnung läuft danach
   * wie bei einer manuellen Korrektur über „Erkennung bearbeiten“.
   */
  @Input() importListenTyp: ListenTyp | null = null;


  @Output()
  importVorbereitet = new EventEmitter<VorbereiteterKiImport>();

  @Output()
  vorbereitungUngueltig = new EventEmitter<void>();

  get bereitZurUebernahme(): boolean {
    return this.canImport
      && !this.isPreviewing
      && !this.vorschauLaeuft
      && !this.vorschauVeraltet
      && !this.relevanzLaeuft
      && !!this.vorschau
      && !this.vorschauFehler;
  }

  /**
   * Warum die Vorschau noch nicht übernommen werden kann – null, wenn alles bereit ist.
   * Für die Fehlermeldung des Mehrfachimports.
   */
  get uebernahmeHinweis(): string | null {
    if (!this.file) {
      return 'Es wurde keine Datei gelesen.';
    }

    if (this.isPreviewing) {
      return 'Die Datei wird noch analysiert.';
    }

    if (this.errorMessage) {
      return this.errorMessage;
    }

    if (!this.listenName.trim()) {
      return 'Bitte einen Namen für die Liste eingeben.';
    }

    if (this.analysis && this.keineSpalteGewaehlt) {
      return 'Es ist keine Spalte zum Import ausgewählt.';
    }

    if (!this.canImport) {
      return 'Blatt und Kopfzeile sind noch nicht festgelegt.';
    }

    if (this.relevanzLaeuft) {
      return 'Die Zeilenprüfung läuft noch.';
    }

    if (this.vorschauLaeuft || this.vorschauVeraltet) {
      return 'Die Vorschau wird noch aktualisiert.';
    }

    if (this.vorschauFehler) {
      return this.vorschauFehler;
    }

    if (!this.vorschau) {
      return 'Die Vorschau ist noch nicht geladen.';
    }

    return null;
  }

  /** Analyse nach einem Fehler erneut starten (eingebettet: Knopf „Erneut versuchen“). */
  erneutAnalysieren(): void {
    if (this.file) {
      this.dateiAnalysieren(this.file);
    }
  }

  /** Vorgabe des Assistenten auf die erkannte Struktur anwenden – wie eine manuelle Typkorrektur. */
  private uebernehmeVorgabeListenTyp(): void {
    if (!this.analysis || !this.importListenTyp || this.analysis.listType === this.importListenTyp) {
      return;
    }

    this.analysis.listType = this.importListenTyp;
    this.onListenTypGeaendert();
  }

  importVorbereiten(): boolean {
    if (!this.file || !this.bereitZurUebernahme) {
      return false;
    }

    this.importVorbereitet.emit({
      file: this.file,
      sheetName: this.analysis?.sheetName || this.selectedSheetName,
      headerRowNumber: this.headerRowNumber,
      listenName: this.listenName.trim(),
      customerId: this.customerId,
      projektId: this.projektId,
      analysis: this.analysis
        ? structuredClone(this.analysis)
        : null,
      regeln: structuredClone(this.baueRegeln()),
      entscheidungen: structuredClone(this.baueEntscheidungen()),
      listenTyp: this.analysis?.listType ?? this.importListenTyp ?? undefined,
      zeilenImport: this.zeilenImport,
      zeilenAusgeschlossen: this.zeilenAus
    });

    return true;
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['importProjektId'] && this.importProjektId) {
      this.projektId = this.importProjektId;
      this.projektName = this.importProjektName;
    }

    if (changes['importDatei'] && this.importDatei && this.importDatei !== this.file) {
      this.dateiAnalysieren(this.importDatei);
    }

    if (changes['importListenTyp'] && !changes['importListenTyp'].firstChange) {
      this.uebernehmeVorgabeListenTyp();
    }
  }

  file: File | null = null;
  preview: ExcelPreview | null = null;

  /** Erkannte Struktur. Null = manueller Weg. */
  analysis: ExcelAiAnalysis | null = null;

  /** Hinweis, wenn die Erkennung nicht möglich war oder verworfen wurde. */
  aiMessage = '';

  /** Für die Auswahlfelder im Template. */
  readonly kennzeichnungsArten = KENNZEICHNUNGS_ARTEN;
  readonly listenTypen = LISTEN_TYPEN;
  readonly operatoren = REGEL_OPERATOREN;
  readonly feldKennzeichnung = FELD_KENNZEICHNUNG;

  selectedSheetName = '';
  headerRowNumber = 0;
  listenName = '';
  customerId: number | null = null;

  /** Aus der Query gesetzt, wenn der Import aus einem Projekt heraus gestartet wurde. */
  projektId: number | null = null;
  projektName = '';

  customers: Customer[] = [];

  isPreviewing = false;
  isImporting = false;
  errorMessage = '';

  // ── Was standardmäßig zugeklappt ist ──
  //
  // Der normale Import kommt ohne all das aus: die Erkennung stimmt, die
  // Vorschau zeigt das Ergebnis, fertig. Jede dieser Ansichten ist eine
  // KORREKTURMÖGLICHKEIT und keine Pflichtstation – deshalb steht sie
  // hinter einem Knopf und nicht im Weg.

  /** Listentyp, Blatt, Kennzeichnung, Kopfbereich – aufklappbar. */
  erkennungBearbeiten = false;

  /** Anlagenteil-Spalte, Klartexte und Regeln – aufklappbar. */
  anlagenteileBearbeiten = false;

  /** Kategorieregeln – aufklappbar. */
  kategorienBearbeiten = false;

  /** Der Bereich „Spalten anpassen“ über der Vorschau. */
  spaltenPanelOffen = false;

  /** Suche im Spaltenbereich – bei hundert Spalten unverzichtbar. */
  spaltenSuche = '';

  // ── Regeln ──

  kategorien: MessstelleKategorie[] = [];

  /** Vorschläge der KI, vom Benutzer einzeln zu bestätigen. */
  kategorieVorschlaege: KategorieVorschlag[] = [];

  kategorieRegeln: KategorieRegelentwurf[] = [];

  anlagenteilRegeln: AnlagenteilRegelentwurf[] = [];

  /**
   * 0-basierter Index einer Spalte, aus deren Werten die Anlagenteile
   * entstehen. <c>null</c> = keine Spalte verwenden.
   */
  anlagenteilSpalte: number | null = null;

  /**
   * Excel-Rohwert -> Anzeigename ("UKR12" -> "Absackung").
   *
   * Der Rohwert bleibt unangetastet: er steht weiter in der Zeile und wird am
   * Anlagenteil als Herkunft vermerkt. Hier entsteht nur der Klartext, unter
   * dem der Anlagenteil in Listen, Filtern und im Projektfortschritt
   * erscheint.
   *
   * Ein leerer Eintrag ist eine Aussage ("kein Klartext, nimm den Rohwert")
   * und wird deshalb mitgeschickt – sonst käme ein bewusst gelöschter Name
   * beim nächsten Rechnen aus dem Projektgedächtnis zurück.
   */
  anlagenteilAnzeigenamen: { [excelWert: string]: string } = {};

  // ── Vorschau ──

  /** So kurz wird nach einer Eingabe gewartet, bevor neu gerechnet wird. */
  private static readonly VORSCHAU_VERZOEGERUNG_MS = 400;

  vorschau: ImportVorschau | null = null;

  vorschauLaeuft = false;

  /** Eine Änderung ist noch nicht in die angezeigte Vorschau eingeflossen. */
  vorschauVeraltet = false;

  vorschauFehler = '';

  /** Läuft die Antwort einer überholten Anfrage ein, wird sie verworfen. */
  private vorschauLauf = 0;

  private vorschauTimer: number | null = null;


  constructor(
    private service: InbetriebnahmeService,
    private customerService: CustomerService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar
  ) { }


  ngOnInit(): void {
    this.customerService.getCustomers().subscribe({
      next: customers => this.customers = customers,
      // Die Kundenzuordnung ist optional – ohne Kundenliste bleibt der Import nutzbar.
      error: () => this.customers = []
    });

    // Die Kategorien kommen aus der Datenbank. Nichts davon ist hier fest
    // verdrahtet – die KI darf nur aus genau dieser Menge vorschlagen.
    this.service.getKategorien().subscribe({
      next: kategorien => this.kategorien = kategorien,
      error: () => this.kategorien = []
    });

    // Aus einem Projekt heraus gestartet: Projekt und Name werden mitgegeben.
    // Der Kunde ergibt sich dann serverseitig aus dem Projekt, deshalb wird die
    // Kundenauswahl in der Ansicht ausgeblendet.
    const projekt = Number(this.route.snapshot.queryParamMap.get('projektId'));
    if (projekt) {
      this.projektId = projekt;
      this.projektName = this.route.snapshot.queryParamMap.get('projektName') ?? '';
    }

  }

  get selectedSheet(): ExcelSheetPreview | null {
    if (!this.preview) {
      return null;
    }
    return this.preview.sheets.find(s => s.name === this.selectedSheetName) ?? null;
  }

  /** Spaltenüberschriften der aktuell gewählten Kopfzeile. */
  get columns(): string[] {
    const sheet = this.selectedSheet;
    if (!sheet) {
      return [];
    }
    const row = sheet.firstRows.find(r => r.rowNumber === this.headerRowNumber);
    return row ? row.cells : sheet.columns;
  }

  /** Datenzeilen unterhalb der gewählten Kopfzeile, für die Vorschautabelle. */
  get dataRows(): string[][] {
    const sheet = this.selectedSheet;
    if (!sheet) {
      return [];
    }
    return sheet.firstRows
      .filter(r => r.rowNumber > this.headerRowNumber)
      .map(r => r.cells);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selected = input.files?.[0] ?? null;

    input.value = '';

    if (selected) {
      this.dateiAnalysieren(selected);
    }
  }

  private dateiAnalysieren(selected: File): void {
    this.file = selected;
    this.preview = null;
    this.analysis = null;
    this.aiMessage = '';
    this.errorMessage = '';
    this.isPreviewing = true;
    this.kategorieVorschlaege = [];
    this.vorschau = null;
    this.vorschauFehler = '';
    this.anlagenteilRegeln = [];
    this.kategorieRegeln = [];
    this.anlagenteilSpalte = null;
    this.anlagenteilAnzeigenamen = {};
    this.relevanz = null;
    this.statusIndex = new Map();
    this.relevanzAutomatischGestartet = false;
    this.relevanzFehler = '';
    this.relevanzSuche = '';
    this.relevanzFilter = 'alle';
    this.zeilenBearbeitung = false;
    this.erkennungBearbeiten = false;
    this.anlagenteileBearbeiten = false;
    this.kategorienBearbeiten = false;
    this.spaltenPanelOffen = false;
    this.spaltenSuche = '';
    this.sichtbareVorschauZeilen = MessstellenImportComponent.ZEILEN_SCHRITT;
    this.vorschauZeilenIndex = new Map();
    this.anzeigeZeilen = [];
    this.vorschauSpalten = [];
    this.gefilterteAnzahl = 0;

    if (!this.listenName) {
      this.listenName = selected.name.replace(/\.(xlsx|xlsm|xls)$/i, '');
    }

    // Ein Aufruf liefert Vorschau UND Strukturerkennung. Scheitert die Erkennung,
    // ist das kein Fehler – die Vorschau kommt trotzdem und der manuelle Weg bleibt.
    this.service.analyze(selected).subscribe({
      next: result => {
        this.preview = result.preview;
        this.isPreviewing = false;
        this.kategorieVorschlaege = result.kategorieVorschlaege ?? [];

        if (result.aiAvailable && result.analysis) {
          this.applyAnalysis(result.analysis);
          this.uebernehmeVorgabeListenTyp();
        } else {
          this.aiMessage = result.aiMessage ?? '';
          // Standardmäßig das erste Blatt wählen, in dem überhaupt etwas erkannt wurde.
          const usable = result.preview.sheets.find(s => s.headerRowNumber > 0) ?? result.preview.sheets[0];
          if (usable) {
            this.selectSheet(usable.name);
          }
        }
      },
      error: err => {
        this.isPreviewing = false;
        this.errorMessage = err?.error?.message ?? 'Die Datei konnte nicht gelesen werden.';
      }
    });
  }

  selectSheet(name: string): void {
    this.selectedSheetName = name;
    const sheet = this.selectedSheet;
    this.headerRowNumber = sheet && sheet.headerRowNumber > 0
      ? sheet.headerRowNumber
      : (sheet?.firstRows[0]?.rowNumber ?? 1);

    this.vorschauAktualisieren();
  }

  // ── Erkannte Struktur ──

  /** Übernimmt die Erkennung und stellt Blatt/Kopfzeile passend ein. */
  private applyAnalysis(analysis: ExcelAiAnalysis): void {
    this.analysis = analysis;
    this.selectedSheetName = analysis.sheetName;
    // Die einzelne Kopfzeile bleibt als Rückfallwert gesetzt, falls der Nutzer
    // die Erkennung verwirft.
    this.headerRowNumber = analysis.headerStartRow;

    this.normalisiereZuordnungen();
    this.sichereAuswahl();

    /*
     * Hat die KI eine Spalte gefunden, deren Werte die Zeilen in
     * Anlagenbereiche gruppieren, wird sie vorgeschlagen. Bestätigt wird sie
     * vom Benutzer – sichtbar in der Vorschau, bevor irgendetwas entsteht.
     */
    this.anlagenteilSpalte =
      analysis.suggestedPlantPartColumn != null
        && analysis.suggestedPlantPartColumn >= 0
        ? analysis.suggestedPlantPartColumn
        : null;

    // Aus jedem Kategorievorschlag wird eine Regelzeile, die der Benutzer
    // prüft. Nur Vorschläge zu Kategorien, die es wirklich gibt.
    this.kategorieRegeln = this.kategorieVorschlaege
      .map(v => ({
        feld: normalisiereStandardFeld(v.feld, analysis.listType) ?? v.feld,
        operator: v.operator,
        wert: v.wert,
        treffer: null as number | null,
        kategorieId: this.kategorien
          .find(k => k.name.toLowerCase() === v.kategorieName.toLowerCase())?.id ?? null
      }))
      .filter(r => r.kategorieId != null);

    this.vorschauAktualisieren();
  }

  /**
   * Bringt jede Zuordnung auf den aktuellen Katalognamen.
   *
   * Ohne diesen Schritt zeigt das Auswahlfeld LEER, sobald der Server einen
   * älteren oder gleichbedeutenden Namen liefert ("AKS/KKS" statt
   * "Kennzeichnung") – und beim Import ginge die Zuordnung still verloren,
   * ohne dass es jemandem auffällt. Ein Wert, der sich nirgends einordnen
   * lässt, bleibt stehen und wird als eigene Option angeboten.
   */
  private normalisiereZuordnungen(): void {
    if (!this.analysis) {
      return;
    }

    for (const spalte of this.analysis.columns) {
      spalte.mappedField = normalisiereStandardFeld(
        spalte.mappedField,
        this.analysis.listType);
    }
  }

  /**
   * Stellt sicher, dass jede Spalte eine Auswahl HAT.
   *
   * Der Server setzt sie; ein älterer Server oder eine von Hand
   * zusammengesetzte Analyse tut es nicht. Dann ist `undefined` das
   * gefährlichste, was passieren kann – eine Spalte wäre weder gewählt noch
   * abgewählt und fiele beim Import lautlos weg. Der Rückfall ist deshalb
   * "zugeordnet = wird importiert".
   */
  private sichereAuswahl(): void {
    if (!this.analysis) {
      return;
    }

    for (const spalte of this.analysis.columns) {
      if (typeof spalte.isIncluded !== 'boolean') {
        spalte.isIncluded = !!spalte.mappedField;
      }
    }
  }

  /**
   * Alle beurteilten Arbeitsblätter, relevante zuerst.
   *
   * Eine Datei kann mehrere fachlich relevante Listen enthalten (typisch:
   * "Verbraucher" und "Messstellen" nebeneinander). Blind das erste Blatt zu
   * importieren wäre in so einem Fall schlicht falsch.
   */
  get gefundeneBlaetter(): ExcelAiSheetAnalysis[] {
    return this.analysis?.sheets ?? [];
  }

  get relevanteBlaetter(): ExcelAiSheetAnalysis[] {
    return this.gefundeneBlaetter.filter(s => s.relevant);
  }

  /** Nur zeigen, wenn es überhaupt etwas zu entscheiden gibt. */
  get zeigeBlattauswahl(): boolean {
    return this.relevanteBlaetter.length > 1;
  }

  /**
   * Wechselt das zu importierende Blatt. Die Beurteilung dieses Blatts wird
   * dabei komplett übernommen – Listentyp, Kennzeichnungsart, Kopfbereich und
   * Spaltenzuordnung gehören zusammen.
   */
  waehleBlatt(blatt: ExcelAiSheetAnalysis): void {
    if (!this.analysis || this.analysis.sheetName === blatt.sheetName) {
      return;
    }

    this.analysis = {
      ...this.analysis,
      sheetName: blatt.sheetName,
      headerStartRow: blatt.headerStartRow,
      headerEndRow: blatt.headerEndRow,
      dataStartRow: blatt.dataStartRow,
      columns: blatt.columns,
      reasoning: blatt.reasoning,
      listType: blatt.listType,
      listTypeConfidence: blatt.listTypeConfidence,
      identifierType: blatt.identifierType,
      identifierTypeConfidence: blatt.identifierTypeConfidence,
      warnungen: blatt.warnungen
    };

    this.selectedSheetName = blatt.sheetName;
    this.headerRowNumber = blatt.headerStartRow;

    this.normalisiereZuordnungen();
    this.sichereAuswahl();
    this.vorschauAktualisieren();
  }

  istGewaehltesBlatt(blatt: ExcelAiSheetAnalysis): boolean {
    return this.analysis?.sheetName === blatt.sheetName;
  }

  blattTypText(blatt: ExcelAiSheetAnalysis): string {
    return this.listenTypen.find(t => t.wert === blatt.listType)?.text ?? 'Unbekannt';
  }

  /**
   * Wie viele Datenzeilen das Blatt hat.
   *
   * Kommt aus der Dateivorschau, nicht aus der Erkennung – gebraucht wird sie
   * bei der Auswahl: enthält eine Datei neben der echten Liste ein Muster- oder
   * Vorlagenblatt, ist die Zeilenzahl oft das, was beide unterscheidet.
   */
  blattZeilen(blatt: ExcelAiSheetAnalysis): number {
    return this.preview?.sheets.find(s => s.name === blatt.sheetName)?.dataRowCount ?? 0;
  }

  /**
   * Die zur Auswahl stehenden Standardfelder. Sie hängen am Listentyp: eine
   * Verbraucherliste kennt Nennleistung und Motortyp, aber keinen Messbereich.
   *
   * Zusätzlich stehen Werte darin, die gerade zugeordnet sind, aber nicht im
   * Katalog vorkommen. Ohne sie zeigte das Auswahlfeld leer und die Zuordnung
   * verschwände beim Import unbemerkt – sichtbar falsch ist besser als
   * unsichtbar weg.
   */
  get standardFelder(): string[] {
    const katalog = standardFelder(this.analysis?.listType);

    if (this.fremdeFelder.length === 0) {
      return katalog;
    }

    return [...katalog, ...this.fremdeFelder];
  }

  /** Zugeordnete Werte, die im Katalog des Listentyps nicht vorkommen. */
  get fremdeFelder(): string[] {
    if (!this.analysis) {
      return [];
    }

    const katalog = new Set(
      standardFelder(this.analysis.listType).map(f => f.toLowerCase()));

    const fremd: string[] = [];

    for (const spalte of this.analysis.columns) {
      const feld = spalte.mappedField;

      if (feld
        && !katalog.has(feld.toLowerCase())
        && !fremd.some(f => f.toLowerCase() === feld.toLowerCase())) {
        fremd.push(feld);
      }
    }

    return fremd;
  }

  /** Gehört das Feld nicht zum Katalog des Listentyps? */
  istFremdesFeld(feld: string | null | undefined): boolean {
    return !!feld && this.fremdeFelder.some(
      f => f.toLowerCase() === feld.toLowerCase());
  }

  /**
   * Wechselt der Benutzer den Listentyp, gilt ein anderer Feldkatalog.
   *
   * Die Zuordnungen werden erneut normalisiert: was im neuen Katalog einen
   * Namen oder Alias hat, wandert dorthin. Was übrig bleibt, wird NICHT
   * gelöscht, sondern als fremdes Feld sichtbar gehalten – der Benutzer sieht
   * so, was er entscheiden muss, statt stillschweigend eine Zuordnung zu
   * verlieren.
   */
  onListenTypGeaendert(): void {
    this.normalisiereZuordnungen();
    this.vorschauAktualisieren();
  }

  /** Beschriftung der Kennzeichnung, wie sie später überall auftaucht. */
  get kennzeichnungAnzeige(): string {
    return kennzeichnungLabel(this.analysis?.identifierType);
  }

  /** Prozentwert der Listentyp-Erkennung, für die Anzeige. */
  get listenTypProzent(): number {
    return Math.round((this.analysis?.listTypeConfidence ?? 0) * 100);
  }

  get kennzeichnungProzent(): number {
    return Math.round((this.analysis?.identifierTypeConfidence ?? 0) * 100);
  }

  /** Erkennung verwerfen und wie bisher manuell wählen. */
  discardAnalysis(): void {
    this.analysis = null;
    this.aiMessage = 'Die automatische Erkennung wurde verworfen. Bitte Blatt und Kopfzeile manuell wählen.';
    this.erkennungBearbeiten = false;
    this.spaltenPanelOffen = false;

    /*
     * Mit dem Kopfbereich verschieben sich die Zeilennummern – die
     * Beurteilungen zeigten danach auf andere Zeilen. Lieber gar kein
     * Zustand als ein falscher: ohne Prüfung wird wie bisher alles
     * importiert.
     */
    this.relevanz = null;
    this.statusIndex = new Map();
    this.relevanzAutomatischGestartet = false;
    this.zeilenBearbeitung = false;

    const usable = this.preview?.sheets.find(s => s.headerRowNumber > 0) ?? this.preview?.sheets[0];
    if (usable) {
      this.selectSheet(usable.name);
    }

    this.vorschauAktualisieren();
  }


  // =========================================================
  // ERKENNUNG – KOMPAKTE ZUSAMMENFASSUNG
  //
  // Sechs Zeilen statt einer Tabelle mit hundert Zeilen. Was die Erkennung
  // gefunden hat, soll man LESEN können, ohne es zu bearbeiten. Die
  // Bearbeitung ist ein Klick entfernt und ändert an der Erkennung selbst
  // nichts – dieselben Felder wie bisher, nur nicht mehr im Weg.
  // =========================================================

  /** Es werden IMMER alle Spalten importiert – Sichtbarkeit steuert nur die Anzeige. */
  get importierteSpalten(): number {
    return this.analysis ? this.analysis.columns.length : 0;
  }

  get zugeordneteFelder(): number {
    return this.analysis
      ? this.analysis.columns.filter(c => !!c.mappedField).length
      : 0;
  }

  /** Unter diesem Wert lohnt ein prüfender Blick. */
  istUnsicher(spalte: ExcelAiColumn): boolean {
    return spalte.confidence > 0 && spalte.confidence < 0.7;
  }

  get unsichereSpalten(): number {
    return this.analysis
      ? this.analysis.columns.filter(c => this.istUnsicher(c)).length
      : 0;
  }

  confidenceProzent(spalte: ExcelAiColumn): number {
    return Math.round((spalte.confidence ?? 0) * 100);
  }

  trackBySpalte(_index: number, spalte: ExcelAiColumn): number {
    return spalte.columnIndex;
  }

  /** Der Listentyp im Klartext, für die Zusammenfassung. */
  get listenTypText(): string {
    return this.listenTypen.find(t => t.wert === this.analysis?.listType)?.text
      ?? 'Unbekannter Listentyp';
  }

  /** „Kopf Zeile 2 · Daten ab Zeile 3“ – oder mit Bereich, wenn der Kopf mehrzeilig ist. */
  get kopfbereichText(): string {
    if (!this.analysis) {
      return '';
    }

    const kopf = this.analysis.headerEndRow > this.analysis.headerStartRow
      ? `Kopf Zeile ${this.analysis.headerStartRow}–${this.analysis.headerEndRow}`
      : `Kopf Zeile ${this.analysis.headerStartRow}`;

    return `${kopf} · Daten ab Zeile ${this.analysis.dataStartRow}`;
  }

  /** Gibt es an der Erkennung etwas, das einen Blick verdient? */
  get erkennungHatHinweise(): boolean {
    return (this.analysis?.warnungen?.length ?? 0) > 0
      || this.unsichereSpalten > 0
      || this.fremdeFelder.length > 0
      || this.listenTypProzent < 70
      || this.kennzeichnungProzent < 70;
  }

  erkennungBearbeitenUmschalten(): void {
    this.erkennungBearbeiten = !this.erkennungBearbeiten;
  }

  /**
   * Beschriftung eines Standardfelds in der Auswahl. Für die Kennzeichnung
   * wird die erkannte Art mitgeschrieben, damit sichtbar ist, worüber man
   * gerade entscheidet.
   */
  feldAnzeige(feld: string): string {
    return feld === FELD_KENNZEICHNUNG && this.analysis
      && this.analysis.identifierType !== 'Unbekannt'
      ? `${feld} (${this.kennzeichnungAnzeige})`
      : feld;
  }

  get canImport(): boolean {
    if (!this.file || !this.listenName.trim() || this.isImporting) {
      return false;
    }

    if (this.analysis) {
      // Mit Erkennung zählt deren Kopfbereich, nicht die einzelne Kopfzeile.
      return !!this.analysis.sheetName
        && this.analysis.headerStartRow > 0
        && this.analysis.headerEndRow >= this.analysis.headerStartRow
        && this.analysis.dataStartRow > this.analysis.headerEndRow
        // Eine Liste ohne eine einzige Spalte wäre eine Liste ohne Inhalt.
        && !this.keineSpalteGewaehlt;
    }

    return !!this.selectedSheetName && this.headerRowNumber > 0;
  }


  // =========================================================
  // SPALTEN ANPASSEN
  //
  // Eine Liste, kein Formular: Excel-Spalte, erkanntes Standardfeld,
  // Häkchen. Nichts sonst.
  //
  // Das Häkchen ist die ENDGÜLTIGE Entscheidung: abgewählt heisst weder
  // anzeigen noch importieren. Die Werte einer abgewählten Spalte werden
  // nicht gespeichert, und ein Standardfeld, das nur an ihr hängt, auch
  // nicht.
  //
  // Die Reihenfolge ist der Punkt: Die KI-Erkennung ist zu diesem Zeitpunkt
  // längst gelaufen und hat ALLE Spalten des Blatts gesehen – Listentyp,
  // Kennzeichnungsart und semantische Feldzuordnung stehen. Die Auswahl ist
  // die letzte Station davor, nicht die erste.
  //
  // Während des Imports rechnen Kategorie- und Anlagenteilregeln noch auf
  // den vollständigen Excel-Werten: "Langtext enthält Druck" greift auch
  // dann, wenn die Spalte "Beschriftung" anschließend nicht gespeichert
  // wird. Das Ergebnis der Regel bleibt, die Quellspalte nicht.
  // =========================================================

  spaltenPanelUmschalten(): void {
    this.spaltenPanelOffen = !this.spaltenPanelOffen;

    if (!this.spaltenPanelOffen) {
      this.spaltenSuche = '';
    }

    // Angular muss zuerst das Panel ins DOM einfügen.
    // Danach den Wizard zwingen, sein Target neu zu berechnen.
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }

  /** Die Spalten der Datei, gefiltert über die Suche. */
  get spaltenAuswahl(): ExcelAiColumn[] {
    const alle = this.analysis?.columns ?? [];

    const suche = this.spaltenSuche.trim().toLowerCase();

    if (suche.length === 0) {
      return alle;
    }

    return alle.filter(s =>
      (s.originalName ?? '').toLowerCase().includes(suche)
      || (s.mappedField ?? '').toLowerCase().includes(suche));
  }

  /**
   * Häkchen setzen oder wegnehmen – die Spalte wird importiert oder nicht.
   *
   * Die Vorschau zieht sofort nach: sie liest die Auswahl direkt aus der
   * Analyse und wartet nicht auf den Server. Neu gerechnet wird trotzdem,
   * damit auch die Zuordnungen zu dem passen, was auf dem Schirm steht.
   */
  spalteImportSetzen(spalte: ExcelAiColumn, importieren: boolean): void {
    spalte.isIncluded = importieren;
    this.vorschauAktualisieren();
  }

  /** Das Standardfeld einer Spalte ändern. Die Auswahl bleibt, wie sie ist. */
  spalteFeldSetzen(spalte: ExcelAiColumn, feld: string | null): void {
    spalte.mappedField = feld;

    /*
     * Ein Standardfeld ist der Grund, eine Spalte mitzunehmen: wer gerade
     * eines vergibt, will die Spalte fast immer auch importieren. Weggenommen
     * wird das Häkchen dabei nie – das bliebe sonst als stille Nebenwirkung
     * hängen und die Spalte fiele beim Import weg.
     */
    if (feld) {
      spalte.isIncluded = true;
    }

    this.vorschauAktualisieren();
  }

  /** Alle gerade gefilterten Spalten auf einmal aufnehmen oder ausschließen. */
  spaltenAuswahlSetzen(importieren: boolean): void {
    for (const spalte of this.spaltenAuswahl) {
      spalte.isIncluded = importieren;
    }

    this.vorschauAktualisieren();
  }

  /** Zurück zum Vorschlag der Erkennung: zugeordnete Spalten rein, der Rest nicht. */
  spaltenVorschlagWiederherstellen(): void {
    for (const spalte of this.analysis?.columns ?? []) {
      spalte.isIncluded = !!spalte.mappedField;
    }

    this.vorschauAktualisieren();
  }

  /**
   * Mindestens eine Spalte muss mit. Eine Liste ohne jede Spalte wäre eine
   * Liste ohne Inhalt – der Server weist sie ab, und der Knopf soll gar nicht
   * erst dorthin führen.
   */
  get keineSpalteGewaehlt(): boolean {
    return !!this.analysis
      && this.analysis.columns.length > 0
      && !this.analysis.columns.some(c => c.isIncluded);
  }


  // =========================================================
  // REGELN: KATEGORIEN UND ANLAGENTEILE
  //
  // Beide arbeiten auf SPALTENWERTEN, nicht auf Spalten: "jede Zeile, deren
  // Kennzeichnung CT enthält, bekommt die Kategorie Messgerät". Die Regeln
  // gehen zusammen mit der Datei an die Vorschau und werden dort auf ALLE
  // Zeilen angewendet – die Trefferzahl ist damit echt und nicht aus zehn
  // Beispielzeilen geschätzt.
  //
  // Angesprochen wird das STANDARDFELD, nicht die Excel-Spalte. Deshalb ist
  // es gleichgültig, ob die Datei "Bezeichnung", "Beschriftung" oder
  // "Description" schreibt: was als "Langtext" erkannt wurde, IST hier der
  // Langtext – auch wenn die Spalte ausgeblendet ist.
  // =========================================================

  /** Die Felder, auf die eine Regel schauen kann. */
  get regelFelder(): string[] {
    return this.standardFelder;
  }

  /** Beschriftung eines Regelfelds; die Kennzeichnung unter ihrer echten Art. */
  regelFeldAnzeige(feld: string): string {
    return feld === FELD_KENNZEICHNUNG
      ? this.kennzeichnungAnzeige
      : feld;
  }

  /**
   * Die Spalten der Datei – für die Auswahl einer Anlagenteil-Spalte.
   * Angeboten wird der Excel-Name, übergeben wird der Spaltenindex.
   */
  get dateiSpalten(): ExcelAiColumn[] {
    return this.analysis?.columns ?? [];
  }

  kategorieRegelHinzufuegen(): void {
    this.kategorieRegeln.push({
      ...this.leereRegel('Langtext', 'enthaelt'),
      kategorieId: null
    });
  }

  kategorieRegelEntfernen(index: number): void {
    this.kategorieRegeln.splice(index, 1);
    this.vorschauAktualisieren();
  }

  anlagenteilRegelHinzufuegen(): void {
    this.anlagenteilRegeln.push({
      ...this.leereRegel(FELD_KENNZEICHNUNG, 'beginntMit'),
      anlagenteilName: ''
    });
  }

  anlagenteilRegelEntfernen(index: number): void {
    this.anlagenteilRegeln.splice(index, 1);
    this.vorschauAktualisieren();
  }

  private leereRegel(feld: string, operator: RegelOperator): Regelentwurf {
    return { feld, operator, wert: '', treffer: null };
  }

  /**
   * Die Regeln in der Form, die der Server erwartet.
   *
   * Unvollständige Regeln werden weggelassen statt mitgeschickt: eine Regel
   * ohne Wert oder ohne Ziel könnte nichts zuordnen und würde die Trefferzahlen
   * nur durcheinanderbringen.
   */
  private baueRegeln(): ImportRegeln {
    return {
      anlagenteilSpalte: this.anlagenteilSpalte,

      /*
       * Auch leere Einträge gehen mit: sie sagen "hier wurde bewusst kein
       * Klartext gesetzt". Nur wo der Schlüssel FEHLT, darf der Server eine
       * im Projekt bekannte Benennung ergänzen.
       */
      anlagenteilAnzeigenamen: this.anlagenteilSpalte == null
        ? {}
        : { ...this.anlagenteilAnzeigenamen },

      anlagenteile: this.aktiveAnlagenteilRegeln.map(r => ({
        feld: r.feld,
        operator: r.operator,
        wert: r.wert.trim(),
        anlagenteilName: r.anlagenteilName.trim()
      })),

      kategorien: this.aktiveKategorieRegeln.map(r => ({
        feld: r.feld,
        operator: r.operator,
        wert: r.wert.trim(),
        kategorieId: r.kategorieId
      }))
    };
  }

  private get aktiveAnlagenteilRegeln(): AnlagenteilRegelentwurf[] {
    return this.anlagenteilRegeln.filter(
      r => r.wert.trim().length > 0 && r.anlagenteilName.trim().length > 0);
  }

  private get aktiveKategorieRegeln(): KategorieRegelentwurf[] {
    return this.kategorieRegeln.filter(
      r => r.wert.trim().length > 0 && r.kategorieId != null);
  }

  /** Wie viele Regeln gerade wirklich etwas tun – für die kompakte Zeile. */
  get aktiveKategorieRegelnAnzahl(): number {
    return this.aktiveKategorieRegeln.length;
  }

  get aktiveAnlagenteilRegelnAnzahl(): number {
    return this.aktiveAnlagenteilRegeln.length;
  }

  anlagenteileBearbeitenUmschalten(): void {
    this.anlagenteileBearbeiten = !this.anlagenteileBearbeiten;
  }

  kategorienBearbeitenUmschalten(): void {
    this.kategorienBearbeiten = !this.kategorienBearbeiten;
  }

  /** Name der Spalte, aus der die Anlagenteile kommen – für die kompakte Zeile. */
  get anlagenteilSpaltenAnzeige(): string {
    if (this.anlagenteilSpalte == null) {
      return '';
    }

    return this.dateiSpalten
      .find(s => s.columnIndex === this.anlagenteilSpalte)?.originalName ?? '';
  }

  /** Wie viele verschiedene Anlagenteile beim Import entstünden. */
  get anlagenteileAnzahl(): number {
    return this.vorschau?.neueAnlagenteile.length ?? 0;
  }

  /** Wie viele Zeilen eine Kategorie bekämen. */
  get zeilenMitKategorie(): number {
    return this.vorschau?.mitKategorie ?? 0;
  }


  // =========================================================
  // VOLLSTÄNDIGE VORSCHAU
  //
  // Sie zeigt, wie die Liste NACH dem Import aussieht: die endgültigen
  // Spaltenüberschriften, echte Zeilen und die Zuordnungen, die die Regeln
  // erzeugen. Gerechnet wird serverseitig auf demselben Weg wie beim Import.
  // Gespeichert wird dabei nichts.
  // =========================================================

  /**
   * Holt die Vorschau neu.
   *
   * Aufgerufen nach jeder Änderung an Mapping, Sichtbarkeit,
   * Kennzeichnungsart, Listentyp oder Regeln. Damit ein Tippen im Wertfeld
   * nicht bei jedem Zeichen eine Anfrage auslöst, wird kurz gewartet; eine
   * überholte Antwort wird verworfen.
   */
  vorschauAktualisieren(): void {
    if (!this.file) {
      return;
    }

    this.vorschauVeraltet = true;

    // Eingebettet: jede Änderung macht eine bereits bestätigte Vorbereitung ungültig.
    if (this.eingebettet) {
      this.vorbereitungUngueltig.emit();
    }

    // Die Sichtbarkeit wirkt sofort – sie hängt nicht an der Antwort.
    this.zeilenNeuAufbauen();

    if (this.vorschauTimer !== null) {
      clearTimeout(this.vorschauTimer);
    }

    this.vorschauTimer = setTimeout(
      () => this.ladeVorschau(),
      MessstellenImportComponent.VORSCHAU_VERZOEGERUNG_MS
    ) as unknown as number;
  }

  private ladeVorschau(): void {
    if (!this.file || !this.canImport) {
      return;
    }

    const lauf = ++this.vorschauLauf;

    this.vorschauLaeuft = true;
    this.vorschauFehler = '';

    this.service.vorschau(
      this.file,
      this.analysis?.sheetName || this.selectedSheetName,
      this.headerRowNumber,
      this.analysis,
      this.baueRegeln(),
      // 0 = alle Zeilen. Die Vorschau ist die Stelle, an der je Zeile
      // entschieden wird, ob sie importiert wird – dafür muss jede Zeile
      // sichtbar sein und nicht nur die ersten fünfundzwanzig.
      0,
      // Damit der Server "UKR12 heisst hier Absackung" aus einer früheren
      // Inbetriebnahme desselben Projekts vorschlagen kann.
      this.projektId
    ).subscribe({
      next: vorschau => {
        // Eine überholte Antwort darf die neuere nicht überschreiben.
        if (lauf !== this.vorschauLauf) {
          return;
        }

        this.vorschau = vorschau;
        this.vorschauLaeuft = false;
        this.vorschauVeraltet = false;

        this.uebernehmeTreffer(vorschau);
        this.uebernehmeAnlagenteilWerte(vorschau);
        this.ergaenzeSpaltenAusVorschau(vorschau);

        this.vorschauZeilenIndex = new Map(
          (vorschau.zeilen ?? []).map(z => [z.rowNumber, z] as const));

        this.zeilenNeuAufbauen();

        /*
         * Die erste Zeilenprüfung läuft von selbst, sobald die Struktur steht.
         * Ohne sie zeigte die Vorschau alle Zeilen als "wird importiert" und
         * die Zeilenauswahl daneben etwas anderes – genau der Widerspruch,
         * den es hier nicht geben darf.
         *
         * Nur EINMAL je Datei: die Vorschau wird bei jeder Regeländerung neu
         * gerechnet, die Prüfung kostet aber Geld. Danach entscheidet der
         * Benutzer über "Erneut prüfen".
         */
        if (this.analysis && !this.relevanzAutomatischGestartet) {
          this.relevanzAutomatischGestartet = true;
          this.zeilenPruefen();
        }
      },
      error: err => {
        if (lauf !== this.vorschauLauf) {
          return;
        }

        this.vorschauLaeuft = false;
        this.vorschauVeraltet = false;
        this.vorschau = null;
        this.vorschauZeilenIndex = new Map();
        this.zeilenNeuAufbauen();
        this.vorschauFehler =
          err?.error?.message ?? 'Die Vorschau konnte nicht erstellt werden.';
      }
    });
  }

  /**
   * Nimmt Spalten auf, die in der Vorschau stehen, in der Erkennung aber
   * fehlen.
   *
   * Der Server vervollständigt die Spaltenliste bereits bei der Erkennung.
   * Dieser Schritt fängt den Fall danach ab: verschiebt der Benutzer unter
   * „Erkennung bearbeiten“ den Kopfbereich, hat das Blatt plötzlich einen
   * anderen Spaltenschnitt. Die Vorschau rechnet dann schon mit den neuen
   * Spalten, die Erkennung kennt aber noch die alten – und genau die
   * Differenz wäre in „Spalten anpassen“ nicht zu finden und nicht
   * abzuwählen.
   *
   * Es entsteht dabei KEINE zweite Spaltenliste: die Einträge landen in
   * `analysis.columns`, der einen Liste, die auch zum Server zurückgeht.
   */
  private ergaenzeSpaltenAusVorschau(vorschau: ImportVorschau): void {
    if (!this.analysis) {
      return;
    }

    const bekannt = new Set(this.analysis.columns.map(c => c.columnIndex));

    let ergaenzt = false;

    for (const spalte of vorschau.spalten ?? []) {
      if (spalte.columnIndex < 0 || bekannt.has(spalte.columnIndex)) {
        continue;
      }

      bekannt.add(spalte.columnIndex);
      ergaenzt = true;

      this.analysis.columns.push({
        columnIndex: spalte.columnIndex,
        originalName: spalte.name,
        mappedField: spalte.standardFeld ?? null,

        /*
         * Eine Rohspalte startet abgewählt – dieselbe Regel wie bei der
         * Erkennung. Trägt sie ein Standardfeld, wird sie importiert.
         */
        isIncluded: !!spalte.standardFeld,
        confidence: 0
      });
    }

    if (ergaenzt) {
      // Dateireihenfolge, sonst hängen die Nachzügler hinten an.
      this.analysis.columns.sort((a, b) => a.columnIndex - b.columnIndex);
    }
  }

  /**
   * Trägt die Trefferzahlen an den Regeln ein, die sie erzeugt haben.
   * Die Reihenfolge entspricht der, in der sie übergeben wurden.
   */
  private uebernehmeTreffer(vorschau: ImportVorschau): void {
    this.aktiveAnlagenteilRegeln.forEach((regel, i) => {
      regel.treffer = vorschau.anlagenteilTreffer[i] ?? 0;
    });

    this.aktiveKategorieRegeln.forEach((regel, i) => {
      regel.treffer = vorschau.kategorieTreffer[i] ?? 0;
    });
  }


  // =========================================================
  // ANLAGENTEIL: EXCEL-WERT -> ANZEIGENAME
  //
  // "UKR12" ist technisch richtig und für den Monteur nichtssagend. Die
  // Tabelle darunter lässt Klartext vergeben, ohne den Rohwert anzutasten:
  // der bleibt in der Zeile stehen und wird am Anlagenteil als Herkunft
  // vermerkt. Ohne Klartext gilt weiterhin der Rohwert selbst.
  // =========================================================

  /** Die gefundenen Werte der gewählten Spalte, wie sie der Server liefert. */
  get anlagenteilWerte(): AnlagenteilWert[] {
    return this.vorschau?.anlagenteilWerte ?? [];
  }

  /**
   * Übernimmt Benennungen, die der Server aus dem Projekt kannte, in die
   * Eingabefelder – sonst stünden sie in der Vorschau, aber nicht im
   * Formular, und der erste Tastendruck anderswo würde sie verwerfen.
   *
   * Was der Benutzer selbst gesetzt hat, bleibt unangetastet.
   */
  private uebernehmeAnlagenteilWerte(vorschau: ImportVorschau): void {
    for (const wert of vorschau.anlagenteilWerte ?? []) {
      if (wert.ausProjekt && this.anlagenteilAnzeigenamen[wert.wert] == null) {
        this.anlagenteilAnzeigenamen[wert.wert] = wert.anzeigename;
      }
    }
  }

  anlagenteilAnzeigename(wert: string): string {
    return this.anlagenteilAnzeigenamen[wert] ?? '';
  }

  /** Klartext setzen und die Vorschau sofort nachziehen. */
  setAnlagenteilAnzeigename(wert: string, name: string): void {
    this.anlagenteilAnzeigenamen[wert] = name;
    this.vorschauAktualisieren();
  }

  /** Alle Klartexte verwerfen – dann gelten wieder die Excel-Werte. */
  anlagenteilNamenZuruecksetzen(): void {
    this.anlagenteilAnzeigenamen = {};
    this.vorschauAktualisieren();
  }

  get hatAnlagenteilNamen(): boolean {
    return Object.values(this.anlagenteilAnzeigenamen)
      .some(n => (n ?? '').trim().length > 0);
  }

  /** Name der gewählten Anlagenteil-Spalte, so wie er in der Datei steht. */
  private get anlagenteilSpaltenName(): string | null {
    if (this.anlagenteilSpalte == null) {
      return null;
    }

    return this.dateiSpalten
      .find(s => s.columnIndex === this.anlagenteilSpalte)?.originalName ?? null;
  }

  /**
   * Der Excel-Rohwert einer Vorschauzeile – aber nur, wenn er sich vom
   * angezeigten Namen unterscheidet. "UKR12" neben "UKR12" wäre nur doppelt.
   */
  rohwertZuZeile(zeile: VorschauAnzeigeZeile): string | null {
    const spalte = this.anlagenteilSpaltenName;

    if (!spalte) {
      return null;
    }

    const roh = (zeile.werte[spalte] ?? '').trim();

    return roh.length > 0 && roh !== zeile.anlagenteilName ? roh : null;
  }


  // ── Die Spalten der Vorschautabelle ──

  /**
   * Was die Tabelle zeigt: die angehakten Spalten, in der Reihenfolge, die
   * der Server vorgibt (fachlich Wichtiges zuerst).
   *
   * Ein FELD und kein Getter: die Tabelle liest die Liste je Zeile einmal,
   * und bei zweitausend Zeilen wäre ein Getter zweitausend neue Arrays je
   * Zeichenlauf. Neu berechnet wird sie zusammen mit den Zeilen – an
   * denselben Stellen, an denen sich etwas ändern kann.
   */
  vorschauSpalten: ImportVorschauSpalte[] = [];

  /**
   * Gelesen wird die Auswahl aus der ANALYSE und nicht aus der Antwort des
   * Servers: ein Häkchen soll sofort wirken und nicht erst, wenn die nächste
   * Vorschau eingelaufen ist. Fehlt der Spaltenindex – ältere Antwort,
   * manueller Weg –, gilt, was der Server geschickt hat.
   *
   * Die Vorschau zeigt damit GENAU die Spalten, die auch importiert werden.
   */
  private spaltenNeuAufbauen(): void {
    const spalten = this.vorschau?.spalten ?? [];

    this.vorschauSpalten = this.analysis
      ? spalten.filter(s => this.wirdSpalteImportiert(s))
      : spalten;
  }

  private wirdSpalteImportiert(spalte: ImportVorschauSpalte): boolean {
    const erkannt = this.analysis?.columns
      .find(c => c.columnIndex === spalte.columnIndex);

    return erkannt ? erkannt.isIncluded : spalte.isIncluded !== false;
  }

  /** Wie viele Spalten importiert werden. */
  get gewaehlteSpalten(): number {
    if (!this.analysis) {
      return this.vorschau?.spalten.length ?? 0;
    }

    return this.analysis.columns.filter(c => c.isIncluded).length;
  }

  /** Wie viele Spalten der Benutzer ausgeschlossen hat. */
  get ausgeschlosseneSpalten(): number {
    if (!this.analysis) {
      return 0;
    }

    return this.analysis.columns.filter(c => !c.isIncluded).length;
  }


  // ── Zeilen der Vorschau ──
  //
  // EINE Tabelle, zwei Zustände. Im Ansehen ist sie reine Anzeige; im
  // Bearbeiten bekommt sie Häkchen, Filter und Suche. Es gibt keine zweite
  // Tabelle weiter unten, in der dieselben Zeilen noch einmal stehen und
  // auseinanderlaufen könnten.

  private static readonly ZEILEN_SCHRITT = 200;

  /** So viele Zeilen werden gezeichnet; der Rest auf Knopfdruck. */
  sichtbareVorschauZeilen = MessstellenImportComponent.ZEILEN_SCHRITT;

  /** Die fertigen Zeilen der Tabelle – bereits gefiltert und begrenzt. */
  anzeigeZeilen: VorschauAnzeigeZeile[] = [];

  /** Wie viele Zeilen der aktuelle Filter insgesamt trifft. */
  gefilterteAnzahl = 0;

  /** Nachschlagewerk Zeilennummer -> Vorschauzeile. */
  private vorschauZeilenIndex = new Map<number, ImportVorschauZeile>();

  /**
   * Baut die angezeigten Zeilen neu auf.
   *
   * Bewusst kein Getter: die Liste wird je Zeichenlauf mehrfach gelesen, und
   * bei zweitausend Zeilen ist Filtern und Zusammenführen bei jedem
   * Änderungslauf von Angular spürbar. Aufgerufen wird sie an genau den
   * Stellen, an denen sich etwas ändern KANN – neue Vorschau, neue Prüfung,
   * Häkchen, Filter, Suche.
   */
  private zeilenNeuAufbauen(): void {
    // Erst die Spalten: die Suche unten schaut in genau die hinein.
    this.spaltenNeuAufbauen();

    /*
     * Im Bearbeiten ist die Beurteilung die Quelle: sie deckt JEDE Zeile der
     * Datei ab, auch die, die eine sehr grosse Vorschau nicht mehr
     * mitgeschickt hat. Sonst gäbe es Zeilen, über die niemand entscheiden
     * kann. Im Ansehen genügen die Zeilen der Vorschau – sie tragen die
     * Werte, die zu sehen sind.
     */
    const quelle: VorschauAnzeigeZeile[] =
      this.zeilenBearbeitung && this.relevanz
        ? this.relevanz.zeilen.map(z => this.baueZeileAusStatus(z))
        : (this.vorschau?.zeilen ?? []).map(z => this.baueZeileAusVorschau(z));

    const gefiltert = this.zeilenBearbeitung
      ? quelle.filter(z => this.passtZumFilter(z))
      : quelle;

    this.gefilterteAnzahl = gefiltert.length;
    this.anzeigeZeilen = gefiltert.slice(0, this.sichtbareVorschauZeilen);
  }

  private baueZeileAusVorschau(zeile: ImportVorschauZeile): VorschauAnzeigeZeile {
    return {
      rowNumber: zeile.rowNumber,
      werte: zeile.werte,
      kategorieName: zeile.kategorieName,
      anlagenteilName: zeile.anlagenteilName,
      status: this.statusIndex.get(zeile.rowNumber) ?? null
    };
  }

  private baueZeileAusStatus(status: ZeilenRelevanzEintrag): VorschauAnzeigeZeile {
    const ausVorschau = this.vorschauZeilenIndex.get(status.rowNumber);

    return {
      rowNumber: status.rowNumber,

      /*
       * Zeilen jenseits der Vorschaugrenze haben keine vollen Werte. Statt
       * einer leeren Zeile stehen dann die Merkmale da, an denen die
       * Beurteilung hing – genug, um zu entscheiden.
       */
      werte: ausVorschau?.werte ?? status.merkmale ?? {},
      kategorieName: ausVorschau?.kategorieName,
      anlagenteilName: ausVorschau?.anlagenteilName,
      status
    };
  }

  private passtZumFilter(zeile: VorschauAnzeigeZeile): boolean {
    const relevanz = zeile.status?.relevanz ?? 'Relevant';

    const passtFilter =
      this.relevanzFilter === 'alle'
      || (this.relevanzFilter === 'import' && relevanz === 'Relevant')
      || (this.relevanzFilter === 'aus' && relevanz === 'Irrelevant')
      || (this.relevanzFilter === 'unsicher' && relevanz === 'Unsicher');

    if (!passtFilter) {
      return false;
    }

    const suche = this.relevanzSuche.trim().toLowerCase();

    if (suche.length === 0) {
      return true;
    }

    /*
     * Gesucht wird in dem, was der Benutzer SIEHT – den angezeigten Spalten –
     * plus Kennzeichnung und Begründung. Über alle hundert Spalten zu suchen
     * wäre bei jeder Eingabe eine Rechnung über die halbe Datei.
     */
    const teile: string[] = [
      String(zeile.rowNumber),
      zeile.status?.kennzeichnung ?? '',
      zeile.status?.grund ?? '',
      zeile.anlagenteilName ?? '',
      zeile.kategorieName ?? ''
    ];

    for (const spalte of this.vorschauSpalten) {
      teile.push(zeile.werte[spalte.name] ?? '');
    }

    return teile.join(' ').toLowerCase().includes(suche);
  }

  get weitereVorschauZeilen(): number {
    return Math.max(0, this.gefilterteAnzahl - this.anzeigeZeilen.length);
  }

  mehrVorschauZeilenZeigen(): void {
    this.sichtbareVorschauZeilen += MessstellenImportComponent.ZEILEN_SCHRITT;
    this.zeilenNeuAufbauen();
  }

  /**
   * Wie viele Zeilen die Vorschau gar nicht erst mitbekommen hat.
   *
   * Bei sehr grossen Dateien deckelt der Server die Vorschau. Entschieden
   * werden kann über solche Zeilen trotzdem: die Zeilenauswahl arbeitet auf
   * der Beurteilung, und die deckt jede Zeile ab.
   */
  get nichtGezeigteZeilen(): number {
    return Math.max(
      0,
      (this.vorschau?.zeilenGesamt ?? 0) - (this.vorschau?.zeilen.length ?? 0));
  }

  kategorieName(id: number | null): string {
    return this.kategorien.find(k => k.id === id)?.name ?? '';
  }

  trackByAnzeigeZeile(_index: number, zeile: VorschauAnzeigeZeile): number {
    return zeile.rowNumber;
  }


  // =========================================================
  // ZEILEN PRÜFEN
  //
  // Reale Listen enthalten neben den Messstellen Gruppenköpfe, Summenzeilen,
  // mechanische Bauteile und Bemerkungen. Dieser Schritt beurteilt jede Zeile
  // und legt das Ergebnis vor – entschieden wird hier, nicht vom Modell.
  //
  // Er läuft einmal je Datei automatisch, sobald die Struktur steht. Danach
  // nur noch auf Knopfdruck: die Prüfung kostet Geld und wird nicht bei jedem
  // Tastendruck ausgelöst wie die Vorschau.
  // =========================================================

  /**
   * DIE Quelle der Wahrheit je Excel-Zeile.
   *
   * `relevanz.zeilen` ist die einzige Stelle, an der der Zustand einer Zeile
   * steht – Vorschau, Zeilenauswahl, Zähler und der endgültige Import lesen
   * alle hier. `statusIndex` ist nur ein Nachschlagewerk auf dieselben
   * Objekte, damit die Tabelle nicht je Zeile durch ein Array sucht.
   *
   * Deshalb wird beim Umschalten das EINTRAGSOBJEKT geändert und nicht
   * ersetzt: Ansicht und Bearbeitung halten dieselbe Referenz und können
   * nicht auseinanderlaufen.
   */
  relevanz: ZeilenRelevanzErgebnis | null = null;

  private statusIndex = new Map<number, ZeilenRelevanzEintrag>();

  relevanzLaeuft = false;

  relevanzFehler = '';

  /**
   * Die erste Prüfung läuft automatisch, sobald die Struktur steht. Danach
   * nur noch auf Knopfdruck – sonst würde jede Regeländerung eine neue
   * (kostenpflichtige) Prüfung auslösen.
   */
  private relevanzAutomatischGestartet = false;

  /** Filter der Zeilenauswahl. */
  relevanzFilter: 'alle' | 'import' | 'aus' | 'unsicher' = 'alle';

  relevanzSuche = '';


  /**
   * @param erneut Vom Benutzer über „Erneut prüfen“ ausgelöst. Nur dann
   * verwirft der Server seine gemerkten KI-Urteile; die automatische erste
   * Prüfung nutzt sie und schickt unveränderte Zeilen nicht erneut an die KI.
   */
  zeilenPruefen(erneut = false): void {
    if (!this.file || this.relevanzLaeuft) {
      return;
    }

    this.relevanzLaeuft = true;
    this.relevanzFehler = '';

    this.service.zeilenRelevanz(
      this.file,
      this.analysis?.sheetName || this.selectedSheetName,
      this.headerRowNumber,
      this.analysis,
      // Was der Benutzer schon selbst gesetzt hat, geht mit: diese Zeilen
      // werden nicht erneut beurteilt und nicht überschrieben.
      this.baueEntscheidungen().filter(e => e.manuellUeberschrieben),
      null,
      erneut
    ).subscribe({
      next: ergebnis => {
        this.relevanzLaeuft = false;

        /*
         * Frühere Benutzerentscheidungen bleiben stehen. Der Server lässt sie
         * bereits unangetastet; hier wird es noch einmal sichergestellt,
         * damit auch eine erneute Prüfung nichts umwirft, was jemand von Hand
         * gesetzt hat.
         */
        const eigene = new Map(
          (this.relevanz?.zeilen ?? [])
            .filter(z => z.manuellUeberschrieben)
            .map(z => [z.rowNumber, z] as const));

        ergebnis.zeilen = ergebnis.zeilen.map(
          z => eigene.get(z.rowNumber) ?? z);

        this.setzeRelevanz(ergebnis);
        this.sichtbareVorschauZeilen = MessstellenImportComponent.ZEILEN_SCHRITT;
        this.zeilenNeuAufbauen();
      },
      error: err => {
        this.relevanzLaeuft = false;
        this.relevanzFehler =
          err?.error?.message ?? 'Die Zeilenprüfung ist fehlgeschlagen.';
      }
    });
  }


  /** Übernimmt ein Prüfergebnis und baut das Nachschlagewerk neu auf. */
  private setzeRelevanz(ergebnis: ZeilenRelevanzErgebnis): void {
    this.relevanz = ergebnis;

    this.statusIndex = new Map(
      ergebnis.zeilen.map(z => [z.rowNumber, z] as const));
  }


  /**
   * Der Zustand einer Excel-Zeile.
   *
   * Solange keine Prüfung gelaufen ist, gibt es keinen – dann gilt überall
   * derselbe Rückfall: die Zeile wird importiert. Das ist das Verhalten von
   * vor der Zeilenprüfung und bleibt es, wenn sie nicht verfügbar ist.
   */
  zeileStatus(rowNumber: number): ZeilenRelevanzEintrag | null {
    return this.statusIndex.get(rowNumber) ?? null;
  }

  /** Wird diese Excel-Zeile importiert? Die eine Frage, die überall zählt. */
  istEingeschlossen(zeile: VorschauAnzeigeZeile): boolean {
    return zeile.status == null || zeile.status.relevanz === 'Relevant';
  }

  zeileIstUnsicher(zeile: VorschauAnzeigeZeile): boolean {
    return zeile.status?.relevanz === 'Unsicher';
  }

  zeileIstAusgeschlossen(zeile: VorschauAnzeigeZeile): boolean {
    return zeile.status?.relevanz === 'Irrelevant';
  }

  /**
   * Ist die Vorschau im Bearbeitungsmodus?
   *
   * Standardmäßig nicht. Die Beurteilung läuft automatisch, und die meisten
   * Importe brauchen keine Korrektur. Im Ansehen gibt es deshalb KEINE
   * Häkchen – eine Vorschau, in der man aus Versehen etwas umstellt, ist
   * keine Vorschau mehr.
   */
  zeilenBearbeitung = false;

  zeilenBearbeitungUmschalten(): void {
    this.zeilenBearbeitung = !this.zeilenBearbeitung;

    // Beim Öffnen und beim Schließen von vorne anfangen: der letzte Filter
    // sagt nichts über das, was jetzt gesucht wird.
    this.relevanzFilter = 'alle';
    this.relevanzSuche = '';
    this.sichtbareVorschauZeilen = MessstellenImportComponent.ZEILEN_SCHRITT;

    this.zeilenNeuAufbauen();

    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }

  /** Filter oder Suche haben sich geändert. */
  zeilenFilterGeaendert(): void {
    this.sichtbareVorschauZeilen = MessstellenImportComponent.ZEILEN_SCHRITT;
    this.zeilenNeuAufbauen();
  }


  /** Grund und Herkunft der Entscheidung, für den Tooltip in der Vorschau. */
  statusHinweis(zeile: VorschauAnzeigeZeile): string {
    const status = zeile.status;

    if (!status) {
      return '';
    }

    const teile = [this.relevanzText(status)];

    if (status.grund) {
      teile.push(status.grund);
    }

    teile.push(this.quelleText(status));

    return teile.join(' · ');
  }


  // ── Die Zähler ──
  //
  // Sie lesen dieselben Einträge wie Vorschau und Import. Ohne Prüfung gilt
  // der Rückfall aus istEingeschlossen: alles wird importiert.

  get zeilenGesamt(): number {
    return this.relevanz?.gesamt ?? this.vorschau?.zeilenGesamt ?? 0;
  }

  /** Wie viele Zeilen aktuell tatsächlich importiert würden. */
  get zeilenImport(): number {
    if (!this.relevanz) {
      return this.zeilenGesamt;
    }

    return this.relevanz.zeilen.filter(z => z.relevanz === 'Relevant').length;
  }

  get zeilenAus(): number {
    return (this.relevanz?.zeilen ?? [])
      .filter(z => z.relevanz === 'Irrelevant').length;
  }

  get zeilenUnsicher(): number {
    return (this.relevanz?.zeilen ?? [])
      .filter(z => z.relevanz === 'Unsicher').length;
  }


  wirdImportiert(zeile: VorschauAnzeigeZeile): boolean {
    return this.istEingeschlossen(zeile);
  }


  /**
   * Zeile ein- oder ausschalten.
   *
   * Ab hier ist die Entscheidung die des Benutzers und wird von keiner
   * weiteren Prüfung mehr angerührt – auch nicht beim nächsten Import
   * derselben Liste.
   */
  zeileUmschalten(zeile: VorschauAnzeigeZeile, importieren: boolean): void {
    const status = zeile.status;

    if (!status) {
      return;
    }

    status.relevanz = importieren ? 'Relevant' : 'Irrelevant';
    status.quelle = 'Benutzer';
    status.manuellUeberschrieben = true;
    status.confidence = 1;

    // Der Filter kann die Zeile jetzt ausschließen – die Liste zieht nach.
    this.zeilenNeuAufbauen();
  }


  /**
   * Alle Zeilen setzen, die der Filter gerade trifft – auch die, die noch
   * nicht gezeichnet sind. „Angezeigte“ meint die Auswahl, nicht den
   * Bildausschnitt; sonst hinge das Ergebnis daran, wie oft jemand
   * „Weitere Zeilen“ gedrückt hat.
   */
  gefilterteUmschalten(importieren: boolean): void {
    const zeilen = (this.relevanz?.zeilen ?? [])
      .map(z => this.baueZeileAusStatus(z))
      .filter(z => this.passtZumFilter(z));

    for (const zeile of zeilen) {
      const status = zeile.status;

      if (!status) {
        continue;
      }

      status.relevanz = importieren ? 'Relevant' : 'Irrelevant';
      status.quelle = 'Benutzer';
      status.manuellUeberschrieben = true;
      status.confidence = 1;
    }

    this.zeilenNeuAufbauen();
  }


  relevanzText(zeile: ZeilenRelevanzEintrag): string {
    switch (zeile.relevanz) {
      case 'Relevant': return 'wird importiert';
      case 'Irrelevant': return 'ausgeschlossen';
      default: return 'unsicher';
    }
  }

  quelleText(zeile: ZeilenRelevanzEintrag): string {
    switch (zeile.quelle) {
      case 'Benutzer': return 'von Hand';
      case 'Regel': return 'Regel';
      case 'Uebernommen': return 'wie zuletzt';
      default: return 'KI';
    }
  }


  /** Die Entscheidungen in der Form, die der Server erwartet. */
  private baueEntscheidungen(): ZeilenEntscheidung[] {
    return (this.relevanz?.zeilen ?? []).map(z => ({
      rowNumber: z.rowNumber,
      kennzeichnung: z.kennzeichnung,
      fingerprint: z.fingerprint,
      relevanz: z.relevanz,
      grund: z.grund,
      quelle: z.quelle,
      manuellUeberschrieben: z.manuellUeberschrieben
    }));
  }


  /**
   * Der endgültige Import – der EINZIGE Schritt, der etwas speichert.
   *
   * Bis hierher ist in der Datenbank nichts entstanden: die Vorschau rechnet
   * nur. Mitgeschickt werden dieselben Regeln, die die Vorschau erzeugt haben.
   */
  startImport(): void {

    if (this.eingebettet) {
      this.errorMessage =
        'Die Speicherung erfolgt erst im letzten Schritt des Mehrfachimports.';
      return;
    }

    if (!this.file || !this.canImport) {
      return;
    }

    this.isImporting = true;
    this.errorMessage = '';

    this.service.import(
      this.file,
      this.analysis?.sheetName || this.selectedSheetName,
      this.headerRowNumber,
      this.listenName.trim(),
      this.customerId,
      this.projektId,
      this.analysis,
      this.baueRegeln(),
      // Ohne Zeilenprüfung leer – dann importiert der Server wie bisher jede
      // Zeile. Mit Prüfung gilt genau das, was hier auf dem Schirm stand.
      this.baueEntscheidungen()
    ).subscribe({
      next: liste => {
        this.isImporting = false;

        this.snackBar.open(
          `${liste.messstellenCount} Messstellen importiert.`, 'OK', { duration: 4000 });

        this.router.navigate(['/wartung/inbetriebnahme/liste', liste.id]);
      },
      error: err => {
        this.isImporting = false;
        this.errorMessage = err?.error?.message ?? 'Der Import ist fehlgeschlagen.';
      }
    });
  }

  cancel(): void {
    // Zurück dorthin, wo der Import gestartet wurde.
    if (this.projektId) {
      this.router.navigate(['/projekte', this.projektId]);
    } else {
      this.router.navigate(['/wartung/inbetriebnahme']);
    }
  }
}
